import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, useRef, forwardRef, useEffect } from "react";
import { useMantineTheme, useComponentDefaultProps, createStyles, Text, Grid, } from "@mantine/core";
import { Calendar, TimeInput } from "@mantine/dates";
import { useUncontrolled, useMergedRef, upperFirst } from "@mantine/hooks";
import dayjs, { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import useMantineMediaQueries from "../hooks/useMantineMediaQueries";
import DateTimePickerBase from "./DateTimePickerBase";
extend(utc);
extend(timezone);
extend(advancedFormat);
const defaultProps = {
    shadow: "sm",
    transitionDuration: 200,
    labelFormat: "MMMM YYYY",
    initiallyOpened: false,
    name: "date",
    size: "sm",
    dropdownType: "popover",
    dropdownPosition: "flip",
    clearable: true,
    disabled: false,
    fixOnBlur: true,
    withinPortal: false,
    firstDayOfWeek: "sunday",
    openDropdownOnClear: false,
    weekendDays: [],
};
/**
 * Component based on Mantine DatePicker.
 * @see https://github.com/mantinedev/mantine/blob/master/src/mantine-dates/src/components/DatePicker/DatePicker.tsx
 */
const DateTimePicker = forwardRef((props, ref) => {
    const { value, onChange, defaultValue, classNames, styles, shadow, locale, inputFormat, transitionDuration, nextMonthLabel, previousMonthLabel, labelFormat, dayStyle, disableOutsideEvents, minDate, maxDate, excludeDate, initialMonth, initiallyOpened, name, size, dropdownType, dropdownPosition, clearable, disabled, clearButtonLabel, fixOnBlur, allowFreeInput, withinPortal, dateParser, firstDayOfWeek, onFocus, onBlur, amountOfMonths, allowLevelChange, initialLevel, onDropdownClose, onDropdownOpen, hideOutsideDates, hideWeekdays, renderDay, type, openDropdownOnClear, unstyled, weekendDays, yearLabelFormat, nextDecadeLabel, nextYearLabel, previousDecadeLabel, previousYearLabel, utcDisplay, ...others } = useComponentDefaultProps("DateTimePicker", defaultProps, props);
    const theme = useMantineTheme();
    const { isMobile } = useMantineMediaQueries();
    const finalLocale = locale || theme.datesLocale;
    const dateFormat = type === "datetime-local"
        ? "YYYY-MM-DD hh:mm a"
        : inputFormat || `${theme.dateFormat}`;
    const [dropdownOpened, setDropdownOpened] = useState(!!initiallyOpened);
    const calendarSize = size === "lg" || size === "xl" ? "md" : "sm";
    const inputRef = useRef(null);
    const [lastValidValue, setLastValidValue] = useState(defaultValue !== null && defaultValue !== void 0 ? defaultValue : null);
    const [_value, setValue] = useUncontrolled({
        value,
        defaultValue,
        finalValue: null,
        onChange,
    });
    const [stagingValue, setStagingValue] = useState(_value);
    const [calendarMonth, setCalendarMonth] = useState(_value || initialMonth || new Date());
    const [focused, setFocused] = useState(false);
    const [inputState, setInputState] = useState(_value instanceof Date
        ? upperFirst(dayjs(_value).locale(finalLocale).format(dateFormat))
        : "");
    const useDateTimeStyles = createStyles((theme) => ({
        input: {
            paddingTop: "6px",
            paddingBottom: "6px",
            paddingLeft: "12px",
            paddingRight: "80px",
            height: "100%",
            borderRadius: theme.radius.sm,
            marginTop: 0,
            textAlign: "left",
        },
    }));
    const { classes: dateTimeClasses } = useDateTimeStyles();
    const _onDropdownOpen = () => {
        onDropdownOpen === null || onDropdownOpen === void 0 ? void 0 : onDropdownOpen();
    };
    const _onDropdownClose = () => {
        onDropdownClose === null || onDropdownClose === void 0 ? void 0 : onDropdownClose();
    };
    const closeDropdown = () => {
        setDropdownOpened(false);
        onDropdownClose === null || onDropdownClose === void 0 ? void 0 : onDropdownClose();
    };
    const openDropdown = () => {
        setDropdownOpened(true);
        onDropdownOpen === null || onDropdownOpen === void 0 ? void 0 : onDropdownOpen();
    };
    useEffect(() => {
        if (value === null && !focused) {
            setInputState("");
        }
        if (value instanceof Date && !focused) {
            setInputState(upperFirst(dayjs(value).locale(finalLocale).format(dateFormat)));
        }
    }, [value, focused]);
    const handleCalendarValueChange = (date) => {
        const time = stagingValue || new Date();
        date.setHours(time.getHours());
        date.setMinutes(time.getMinutes());
        setStagingValue(date);
        setValue(date);
        setInputState(upperFirst(dayjs(date).locale(finalLocale).format(dateFormat)));
        closeDropdown();
    };
    const handleTimeValueChange = (time) => {
        const date = stagingValue || new Date();
        time.setFullYear(date.getFullYear());
        time.setMonth(date.getMonth());
        time.setDate(date.getDate());
        setStagingValue(time);
        setValue(time);
        setInputState(upperFirst(dayjs(time).locale(finalLocale).format(dateFormat)));
        closeDropdown();
    };
    const handleClear = () => {
        setValue(null);
        setLastValidValue(null);
        setInputState("");
        openDropdownOnClear && openDropdown();
    };
    const parseDate = (date) => dateParser
        ? dateParser(date)
        : dayjs(date, dateFormat, finalLocale).toDate();
    const setDateFromInput = () => {
        let date = typeof _value === "string" ? parseDate(_value) : _value;
        if (maxDate && dayjs(date).isAfter(maxDate)) {
            date = maxDate;
        }
        if (minDate && dayjs(date).isBefore(minDate)) {
            date = minDate;
        }
        if (dayjs(date).isValid()) {
            setValue(date);
            setLastValidValue(date);
            setInputState(upperFirst(dayjs(date).locale(finalLocale).format(dateFormat)));
            setCalendarMonth(date);
        }
        else if (fixOnBlur) {
            setValue(lastValidValue);
        }
    };
    const handleInputBlur = (event) => {
        typeof onBlur === "function" && onBlur(event);
        setFocused(false);
        if (allowFreeInput) {
            setDateFromInput();
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === "Enter" && allowFreeInput) {
            closeDropdown();
            setDateFromInput();
        }
    };
    const handleInputFocus = (event) => {
        typeof onFocus === "function" && onFocus(event);
        setFocused(true);
    };
    const handleChange = (event) => {
        openDropdown();
        const date = parseDate(event.target.value);
        if (dayjs(date).isValid()) {
            setValue(date);
            setLastValidValue(date);
            setInputState(event.target.value);
            setCalendarMonth(date);
        }
        else {
            setInputState(event.target.value);
        }
    };
    const useDayStyles = createStyles((theme) => ({
        weekend: {
            color: `${theme.colors.gray[7]} !important`,
        },
    }));
    const { classes: dayClasses, cx: dayCx } = useDayStyles();
    const { dayClassName: dayClassNameProp } = props;
    let calendarDayClassName = dayClassNameProp;
    if (!calendarDayClassName) {
        calendarDayClassName = (date, modifiers) => dayCx({
            [dayClasses.weekend]: modifiers.weekend,
        });
    }
    return (_jsxs(Grid, { columns: 9, align: "flex-end", gutter: 12, children: [_jsx(Grid.Col, { span: inputState ? "auto" : 9, children: _jsx(DateTimePickerBase, { allowFreeInput: allowFreeInput, dropdownOpened: dropdownOpened, setDropdownOpened: setDropdownOpened, shadow: shadow, transitionDuration: transitionDuration, ref: useMergedRef(ref, inputRef), size: size, styles: styles, classNames: classNames, onChange: handleChange, onBlur: handleInputBlur, onFocus: handleInputFocus, onKeyDown: handleKeyDown, name: name, inputLabel: inputState, __staticSelector: "DateTimePicker", dropdownType: dropdownType, dropdownPosition: dropdownPosition, clearable: type === "date" ? false : clearable && !!_value && !disabled, clearButtonLabel: clearButtonLabel, onClear: handleClear, disabled: disabled, withinPortal: withinPortal, amountOfMonths: amountOfMonths, onDropdownClose: _onDropdownClose, onDropdownOpen: _onDropdownOpen, type: type, unstyled: unstyled, ...others, children: _jsx(Calendar, { classNames: classNames, styles: styles, locale: finalLocale, nextMonthLabel: nextMonthLabel, previousMonthLabel: previousMonthLabel, month: allowFreeInput ? calendarMonth || undefined : undefined, initialMonth: initialMonth || (_value instanceof Date ? _value : new Date()), onMonthChange: setCalendarMonth, value: stagingValue instanceof Date
                            ? stagingValue
                            : dayjs(stagingValue).toDate(), onChange: handleCalendarValueChange, labelFormat: labelFormat, dayClassName: calendarDayClassName, dayStyle: dayStyle, disableOutsideEvents: disableOutsideEvents, minDate: minDate, maxDate: maxDate, excludeDate: excludeDate, __staticSelector: "DateTimePicker", fullWidth: dropdownType === "modal", __stopPropagation: dropdownType !== "modal", size: dropdownType === "modal" ? "lg" : calendarSize, firstDayOfWeek: firstDayOfWeek, preventFocus: allowFreeInput, amountOfMonths: amountOfMonths, allowLevelChange: allowLevelChange, initialLevel: initialLevel, hideOutsideDates: hideOutsideDates, hideWeekdays: hideWeekdays, renderDay: renderDay, unstyled: unstyled, weekendDays: weekendDays, yearLabelFormat: yearLabelFormat, nextDecadeLabel: nextDecadeLabel, nextYearLabel: nextYearLabel, previousDecadeLabel: previousDecadeLabel, previousYearLabel: previousYearLabel }) }) }), inputState && (_jsx(Grid.Col, { span: isMobile ? 9 : "content", children: _jsx(TimeInput, { classNames: { input: dateTimeClasses.input }, onChange: handleTimeValueChange, value: stagingValue, format: "12", rightSectionWidth: 80, rightSection: _jsx(Text, { align: "right", size: "sm", w: "100%", sx: {
                            lineHeight: 1,
                            color: disabled ? theme.colors.dark[2] : "inherit",
                        }, children: utcDisplay ? "UTC" : dayjs(stagingValue).format("z") }), disabled: disabled }) }))] }));
});
export default DateTimePicker;
